// src/pages/Subscription.jsx
import React, { useState, useEffect } from 'react';
import { Switch } from '../components/ui/switch';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Plus, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_BASE_URL = '/api'; //'http://localhost:8002/api'

export const Subscription = () => {
    const [availableAgents, setAvailableAgents] = useState([]);
    const [userSubscriptions, setUserSubscriptions] = useState([]);
    const [savingSubscriptions, setSavingSubscriptions] = useState({}); // 저장 상태 추가
    const [modifiedSubscriptions, setModifiedSubscriptions] = useState(new Set()); // 수정된 구독 추적
    const [telegramLink, setTelegramLink] = useState(null);   // 연동 상태 { linked, ... }
    const [telegramInvite, setTelegramInvite] = useState(null); // 발급된 초대 링크
    const { user } = useAuth();

    // 텔레그램으로 만든 계정은 연동 대상이 아니다 (자기 자신에게 묶는 셈이 된다)
    const isTelegramAccount = (user?.user_id || '').startsWith('telegram:');

    useEffect(() => {
        if (user?.user_id) {
            loadAvailableAgents();
            loadUserSubscriptions();
            if (!isTelegramAccount) loadTelegramLink();
        }
    }, [user]);

    const authHeaders = () => ({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
    });

    const loadTelegramLink = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/telegram/link/status`, { headers: authHeaders() });
            if (!response.ok) {
                // 조회가 실패해도 카드는 남긴다 — 통째로 사라지면 연동 수단이 없어진다.
                console.error('Telegram link status failed:', response.status);
                setTelegramLink({ linked: false });
                return;
            }
            setTelegramLink(await response.json());
        } catch (error) {
            console.error('Error loading telegram link status:', error);
        }
    };

    const handleCreateTelegramInvite = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/telegram/link/invite`, {
                method: 'POST',
                headers: authHeaders(),
            });
            if (!response.ok) throw new Error(await response.text());
            setTelegramInvite(await response.json());
        } catch (error) {
            console.error('Error creating telegram invite:', error);
        }
    };

    // 채널 목록. 값이 없으면 채널별 기본값이 다르므로 계정 종류로 판단한다.
    const channelsOf = (subscription) => {
        const channels = subscription.preferences?.__channels;
        if (Array.isArray(channels)) return channels;
        return isTelegramAccount ? ['telegram'] : ['slack'];
    };

    const handleChannelToggle = async (subscription, channel) => {
        const current = channelsOf(subscription);
        const next = current.includes(channel)
            ? current.filter((c) => c !== channel)
            : [...current, channel];
        try {
            await fetch(`${API_BASE_URL}/telegram/channels`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({
                    agent_name: subscription.agent_name,
                    agent_version: subscription.agent_version,
                    channels: next,
                }),
            });
            await loadUserSubscriptions();
        } catch (error) {
            console.error('Error toggling channel:', error);
        }
    };

    const loadAvailableAgents = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/agents/available?user_id=${user.user_id}`);
            const agents = await response.json();
            setAvailableAgents(agents); // 더 이상 클라이언트에서 필터링하지 않음
        } catch (error) {
            console.error('Error loading available agents:', error);
        }
    };

    const loadUserSubscriptions = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/subscription/user/${user.user_id}`);
            const subscriptions = await response.json();
            setUserSubscriptions(subscriptions);
        } catch (error) {
            console.error('Error loading user subscriptions:', error);
        }
    };

    const handleSubscribe = async (agent) => {
        // agent.input_params의 스키마에 맞춰 기본값 생성
        const defaultPreferences = {};
        if (!agent.input_params)
            agent.input_params = {};
        Object.entries(agent.input_params).forEach(([key, schema]) => {
            if (schema.type === 'array') {
                defaultPreferences[key] = [];
            } else if (schema.type === 'string') {
                defaultPreferences[key] = '';
            } else if (schema.type === 'number' || schema.type === 'integer') {
                defaultPreferences[key] = 0;
            } else if (schema.type === 'boolean') {
                defaultPreferences[key] = false;
            }
        });

        const subscriptionData = {
            agent_name: agent.agent_name,
            agent_version: agent.version,
            input_params: agent.input_params,
            preferences: defaultPreferences
        };

        try {
            const response = await fetch(`${API_BASE_URL}/subscription/${user.user_id}/create`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(subscriptionData)
            });

            if (!response.ok) {
                const error = await response.json();
                console.error('Subscription error:', error);
                return;
            }

            await loadAvailableAgents();
            await loadUserSubscriptions();
        } catch (error) {
            console.error('Error subscribing to agent:', error);
        }
    };


    const handleUnsubscribe = async (subscription) => {
        try {
            await fetch(`${API_BASE_URL}/subscription/${user.user_id}/unsubscribe`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    agent_name: subscription.agent_name,
                    agent_version: subscription.agent_version
                })
            });
            await loadAvailableAgents();
            await loadUserSubscriptions();
        } catch (error) {
            console.error('Error unsubscribing:', error);
        }
    };

    // ButtonGroup 컴포넌트 추가 (TagsInput 컴포넌트 바로 위에)
    const ButtonGroup = ({ options, value, onChange, multiple = false, className = "" }) => {
        const handleOptionClick = (option) => {
            if (multiple) {
                // 다중 선택 모드
                const currentValues = Array.isArray(value) ? value : [];
                if (currentValues.includes(option)) {
                    // 이미 선택된 경우 제거
                    onChange(currentValues.filter(v => v !== option));
                } else {
                    // 선택되지 않은 경우 추가
                    onChange([...currentValues, option]);
                }
            } else {
                // 단일 선택 모드
                onChange(option);
            }
        };

        const isSelected = (option) => {
            if (multiple) {
                return Array.isArray(value) && value.includes(option);
            } else {
                return value === option;
            }
        };
        const selectedCount = multiple && Array.isArray(value) ? value.length : 0;

        return (
            <div className="space-y-2">
                {multiple && selectedCount > 0 && (
                    <div className="text-xs text-gray-500">
                        {selectedCount}개 선택됨
                    </div>
                )}
                <div className={`flex flex-wrap gap-2 ${className}`}>
                    {options.map((option) => (
                        <button
                            key={option}
                            type="button"
                            onClick={() => handleOptionClick(option)}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isSelected(option)
                                ? 'bg-primary-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                        >
                            {option}
                        </button>
                    ))}
                </div>
            </div>
        );

    };


    // TagsInput 컴포넌트 수정
    const TagsInput = ({ tags, onChange, showSuggestions = false }) => {
        const [inputValue, setInputValue] = useState('');
        const [suggestedKeywords, setSuggestedKeywords] = useState([]);
        const [isLoading, setIsLoading] = useState(false);

        // 추천 키워드 불러오기 - showSuggestions가 true일 때만 실행
        useEffect(() => {
            // 추천 키워드 기능이 켜져있을 때만 API 호출
            if (!showSuggestions) return;

            const fetchKeywords = async () => {
                try {
                    setIsLoading(true);
                    const response = await fetch(`${API_BASE_URL}/keywords/cloud`);
                    if (response.ok) {
                        const data = await response.json();
                        // 키워드 클라우드에서 상위 키워드 가져오기 (최대 10개)
                        setSuggestedKeywords(data.keywords.slice(0, 10).map(k => k.text));
                    }
                } catch (error) {
                    console.error('Error loading suggested keywords:', error);
                } finally {
                    setIsLoading(false);
                }
            };

            fetchKeywords();
        }, [showSuggestions]);

        const handleAddTag = () => {
            if (inputValue.trim()) {
                onChange([...tags, inputValue.trim()]);
                setInputValue('');
            }
        };

        const handleRemoveTag = (tagToRemove) => {
            onChange(tags.filter(tag => tag !== tagToRemove));
        };

        // 추천 키워드 클릭 처리
        const handleSuggestedKeywordClick = (keyword) => {
            if (!tags.includes(keyword)) {
                onChange([...tags, keyword]);
            }
        };

        // 이미 선택된 키워드는 필터링
        const filteredSuggestions = suggestedKeywords.filter(
            keyword => !tags.includes(keyword)
        );

        return (
            <div className="space-y-4">
                {/* 추천 키워드 섹션 - showSuggestions가 true일 때만 표시 */}
                {showSuggestions && filteredSuggestions.length > 0 && (
                    <div className="space-y-2">
                        <p className="text-sm text-gray-500">추천 키워드:</p>
                        <div className="flex flex-wrap gap-2 max-w-full overflow-x-auto pb-2">
                            {filteredSuggestions.map((keyword, index) => (
                                <button
                                    key={index}
                                    onClick={() => handleSuggestedKeywordClick(keyword)}
                                    className="inline-flex items-center bg-gray-100 hover:bg-gray-200 text-gray-800 px-3 py-1.5 rounded-full text-sm transition-colors"
                                    type="button"
                                >
                                    {keyword}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* 기존 입력 필드 */}
                <div className="flex gap-2">
                    <Input
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddTag();
                            }
                        }}
                        placeholder={showSuggestions ? "키워드 입력... (영어만 가능)" : "Add new item..."}
                        className="flex-1"
                    />
                    <Button onClick={handleAddTag} size="icon">
                        <Plus className="h-4 w-4" />
                    </Button>
                </div>

                {/* 선택된 태그 표시 */}
                <div className="flex flex-wrap gap-2">
                    {tags.map((tag, index) => (
                        <span
                            key={index}
                            className="inline-flex items-center gap-1 bg-primary-100 text-primary-800 px-2 py-1 rounded-full text-sm"
                        >
                            {tag}
                            <button
                                onClick={() => handleRemoveTag(tag)}
                                className="text-primary-600 hover:text-primary-800"
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </span>
                    ))}
                </div>
            </div>
        );
    };

    // 구독 설정 저장 함수 추가
    const handleSavePreferences = async (subscription) => {
        try {
            setSavingSubscriptions(prev => ({
                ...prev,
                [`${subscription.agent_name}-${subscription.agent_version}`]: true
            }));

            const response = await fetch(`${API_BASE_URL}/subscription/${user.user_id}/update`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    agent_name: subscription.agent_name,
                    agent_version: subscription.agent_version,
                    notification_interval_days: subscription.notification_interval_days,
                    preferences: subscription.preferences
                })
            });

            if (!response.ok) {
                throw new Error('Failed to update subscription');
            }

            // 성공적으로 저장되면 modified 상태 제거
            setModifiedSubscriptions(prev => {
                const newSet = new Set(prev);
                newSet.delete(`${subscription.agent_name}-${subscription.agent_version}`);
                return newSet;
            });

            await loadUserSubscriptions();
        } catch (error) {
            console.error('Error saving preferences:', error);
        } finally {
            setSavingSubscriptions(prev => ({
                ...prev,
                [`${subscription.agent_name}-${subscription.agent_version}`]: false
            }));
        }
    };

    // preferences 변경 핸들러
    const handlePreferenceChange = (subscription, key, newValue) => {
        const newSubscription = {
            ...subscription,
            preferences: {
                ...subscription.preferences,
                [key]: newValue
            }
        };
        setUserSubscriptions(subs =>
            subs.map(sub =>
                sub.agent_name === subscription.agent_name &&
                    sub.agent_version === subscription.agent_version
                    ? newSubscription
                    : sub
            )
        );
        // 수정된 구독 추적
        setModifiedSubscriptions(prev =>
            new Set(prev).add(`${subscription.agent_name}-${subscription.agent_version}`)
        );
    };

    // 값이 문자열 "true"/"false"인 경우 실제 boolean으로 변환
    const getTypedValue = (value) => {
        if (typeof value === 'string') {
            const lowerValue = value.toLowerCase();
            if (lowerValue === "true") return true;
            if (lowerValue === "false") return false;
        }
        return value;
    };

    // 타입에 따라 적절한 입력 컴포넌트를 렌더링
    const renderInputByType = (subscription, key, value, schema) => {
        // 스키마가 있고 choices가 정의된 경우 ButtonGroup 사용
        if (schema && schema.choices && Array.isArray(schema.choices)) {
            return (
                <ButtonGroup
                    options={schema.choices}
                    value={value}
                    onChange={(newValue) => handlePreferenceChange(subscription, key, newValue)}
                    multiple={schema.multiple || false}
                />
            );
        }

        // 스키마가 없는 경우 타입을 유추
        if (!schema) {
            // 배열 타입 처리
            if (Array.isArray(value)) {
                // key가 'keyword_list'인 경우 추천 키워드 기능 활성화
                const isKeywordList = key === 'keyword_list';
                return (
                    <TagsInput
                        tags={value}
                        onChange={(newTags) => handlePreferenceChange(subscription, key, newTags)}
                        showSuggestions={isKeywordList} // 키워드 필드인 경우에만 추천 기능 활성화
                    />
                );
            }

            // 문자열로 저장된 boolean 값 처리
            const typedValue = getTypedValue(value);
            if (typeof typedValue === 'boolean') {
                return (
                    <div className="flex items-center gap-2">
                        <Switch
                            checked={typedValue}
                            onCheckedChange={(checked) => handlePreferenceChange(subscription, key, checked)}
                        />
                        <span className="text-sm text-gray-700">{typedValue ? 'Enabled' : 'Disabled'}</span>
                    </div>
                );
            }

            // 기본 입력 타입 사용
            return (
                <Input
                    type={typeof value === 'number' ? 'number' : 'text'}
                    value={value}
                    onChange={(e) => handlePreferenceChange(
                        subscription,
                        key,
                        e.target.type === 'number' ? parseInt(e.target.value) : e.target.value
                    )}
                />
            );
        }

        // 스키마 타입에 따른 처리
        switch (schema.type.toLowerCase()) {
            case 'boolean':
                const boolValue = typeof value === 'string' ? value === 'true' : !!value;
                return (
                    <div className="flex items-center gap-2">
                        <Switch
                            checked={boolValue}
                            onCheckedChange={(checked) => handlePreferenceChange(subscription, key, checked)}
                        />
                        <span className="text-sm text-gray-700">{boolValue ? 'Enabled' : 'Disabled'}</span>
                    </div>
                );
            case 'array':
                // choices가 있으면 ButtonGroup 사용
                if (schema.choices && Array.isArray(schema.choices)) {
                    return (
                        <ButtonGroup
                            options={schema.choices}
                            value={Array.isArray(value) ? value : []}
                            onChange={(newValue) => handlePreferenceChange(subscription, key, newValue)}
                            multiple={true}
                        />
                    );
                }
                // 기존 TagsInput 로직
                const isKeywordList = key === 'keyword_list';
                return (
                    <TagsInput
                        tags={Array.isArray(value) ? value : []}
                        onChange={(newTags) => handlePreferenceChange(subscription, key, newTags)}
                        showSuggestions={isKeywordList}
                    />
                );
            case 'number':
            case 'integer':
                return (
                    <Input
                        type="number"
                        value={value}
                        onChange={(e) => handlePreferenceChange(subscription, key, parseInt(e.target.value))}
                    />
                );
            case 'string':
            default:
                return (
                    <Input
                        type="text"
                        value={value}
                        onChange={(e) => handlePreferenceChange(subscription, key, e.target.value)}
                    />
                );
        }
    };


    // interval 변경 핸들러
    const handleIntervalChange = (subscription, newInterval) => {
        // 1~30 범위로 제한
        const validInterval = Math.min(Math.max(newInterval, 1), 30);
        const newSubscription = {
            ...subscription,
            notification_interval_days: validInterval
        };
        setUserSubscriptions(subs =>
            subs.map(sub =>
                sub.agent_name === subscription.agent_name &&
                    sub.agent_version === subscription.agent_version
                    ? newSubscription
                    : sub
            )
        );
        // 수정된 구독 추적
        setModifiedSubscriptions(prev =>
            new Set(prev).add(`${subscription.agent_name}-${subscription.agent_version}`)
        );
    };

    return (
        <div className="container mx-auto px-4 py-8 space-y-8">
            {/* Telegram 연동 — 텔레그램으로 가입한 계정에는 보이지 않는다 */}
            {!isTelegramAccount && telegramLink && (
                <Card>
                    <CardHeader>
                        <CardTitle>Telegram 연동</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {telegramLink.linked ? (
                            <p className="text-sm text-gray-700">
                                ✅ 연동됨
                                <br />
                                <span className="text-gray-500">
                                    각 구독의 <b>Telegram Notifications</b> 를 켜면 텔레그램으로 받습니다.
                                </span>
                            </p>
                        ) : telegramInvite ? (
                            <div className="space-y-2">
                                <p className="text-sm text-gray-700">
                                    아래 링크를 <b>본인 텔레그램</b>에서 열어 주세요.
                                    {' '}({telegramInvite.expires_in_minutes}분간 유효)
                                </p>
                                <a href={telegramInvite.link} target="_blank" rel="noreferrer"
                                    className="text-sm text-blue-600 underline break-all">
                                    {telegramInvite.link}
                                </a>
                                <p className="text-xs text-gray-500">
                                    연동을 마친 뒤 이 페이지를 새로고침하면 상태가 갱신됩니다.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <p className="text-sm text-gray-700">
                                    지금 계정의 구독을 그대로 유지한 채 알림을 텔레그램으로 받을 수 있습니다.
                                </p>
                                <Button size="sm" onClick={handleCreateTelegramInvite}>
                                    텔레그램 연동하기
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Available Agents Section */}
            <Card>
                <CardHeader>
                    <CardTitle>구독설정</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4">
                    {availableAgents.map((agent) => (
                        <div key={`${agent.agent_name}-${agent.version}`}
                            className="flex flex-col md:flex-row md:justify-between items-start md:items-center p-4 border rounded-lg gap-4">
                            <div>
                                <h3 className="font-medium">{agent.display_name || agent.agent_name}</h3>
                                <p className="text-sm text-gray-500">Version {agent.version}</p>
                                {agent.description && (
                                    <p className="text-sm mt-2">{agent.description}</p>
                                )}
                            </div>
                            <Button onClick={() => handleSubscribe(agent)}>
                                Subscribe
                            </Button>
                        </div>
                    ))}
                </CardContent>
            </Card>

            {/* My Subscriptions Section */}
            <Card>
                <CardHeader>
                    <CardTitle>내 구독설정</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-6">
                    {userSubscriptions.map((subscription) => {
                        const subscriptionKey = `${subscription.agent_name}-${subscription.agent_version}`;

                        return (
                            <div key={subscriptionKey} className="border rounded-lg p-4 space-y-4 mb-4">
                                <div className="flex flex-col md:flex-row md:justify-between gap-4">
                                    <div>
                                        <h3 className="font-medium">
                                            {subscription.display_name || subscription.agent_name} (v{subscription.agent_version})
                                        </h3>
                                        {subscription.description && (
                                            <p className="text-sm mt-1">{subscription.description}</p>
                                        )}
                                    </div>
                                    <div className="flex gap-2">
                                        {modifiedSubscriptions.has(`${subscription.agent_name}-${subscription.agent_version}`) && (
                                            <Button
                                                onClick={() => handleSavePreferences(subscription)}
                                                disabled={savingSubscriptions[`${subscription.agent_name}-${subscription.agent_version}`]}
                                                className="whitespace-nowrap"
                                            >
                                                {savingSubscriptions[`${subscription.agent_name}-${subscription.agent_version}`]
                                                    ? "Saving..."
                                                    : "Apply Changes"}
                                            </Button>
                                        )}
                                        <Button
                                            variant="destructive"
                                            onClick={() => handleUnsubscribe(subscription)}
                                            className="whitespace-nowrap"
                                        >
                                            Unsubscribe
                                        </Button>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <div>
                                        <h4 className="text-sm font-medium mb-2">Preferences</h4>
                                        {Object.entries(subscription.preferences).map(([key, value]) => {
                                            // 특수 키는 건너뛰기 (__ 로 시작하거나 시스템 관련 키)
                                            if (key.startsWith('__') || key.toLowerCase().includes('enabled')) {
                                                return null;
                                            }

                                            // input_params에서 해당 키의 스키마 정보 찾기
                                            const paramSchema = subscription.input_params &&
                                                subscription.input_params[key];

                                            // 다중 선택 가능 여부 확인
                                            const isMultiple = paramSchema && (
                                                paramSchema.multiple ||
                                                (paramSchema.type === 'array' && paramSchema.choices)
                                            );

                                            return (
                                                <div key={key} className="bg-gray-50 border border-gray-100 rounded-lg p-4 relative mb-2">
                                                    {/* 좌상단 L자 그라데이션 보더 */}
                                                    <div className="absolute top-0 left-0 w-32 h-0.5 bg-gradient-to-r from-blue-400 to-transparent"></div>
                                                    <div className="absolute top-0 left-0 w-0.5 h-16 bg-gradient-to-b from-blue-400 to-transparent"></div>

                                                    <label className="text-sm text-gray-700 font-medium">
                                                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                        {isMultiple && (
                                                            <span className="ml-2 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                                                                복수 선택 가능
                                                            </span>
                                                        )}
                                                    </label>
                                                    {renderInputByType(subscription, key, value, paramSchema)}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div>
                                        <label className="text-sm text-gray-700 block mb-2">
                                            Notification Interval (days)
                                        </label>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <Input
                                                type="number"
                                                min="1"
                                                max="30"
                                                value={subscription.notification_interval_days}
                                                onChange={(e) => handleIntervalChange(subscription, parseInt(e.target.value))}
                                                className="w-32 min-w-32"
                                            />
                                            <div className="flex flex-wrap gap-2">
                                                <Button
                                                    variant={subscription.notification_interval_days === 1 ? "default" : "outline"}
                                                    size="sm"
                                                    onClick={() => handleIntervalChange(subscription, 1)}
                                                    className="min-w-16"
                                                >
                                                    1일
                                                </Button>
                                                <Button
                                                    variant={subscription.notification_interval_days === 3 ? "default" : "outline"}
                                                    size="sm"
                                                    onClick={() => handleIntervalChange(subscription, 3)}
                                                    className="min-w-16"
                                                >
                                                    3일
                                                </Button>
                                                <Button
                                                    variant={subscription.notification_interval_days === 7 ? "default" : "outline"}
                                                    size="sm"
                                                    onClick={() => handleIntervalChange(subscription, 7)}
                                                    className="min-w-16"
                                                >
                                                    7일
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <h4 className="text-sm font-medium">Notifications</h4>
                                        <div className="flex items-center gap-4">
                                            {/* 텔레그램으로 가입한 계정은 Slack 워크스페이스가 없다.
                                                켜도 발송이 실패하므로 아예 감춘다. */}
                                            {!isTelegramAccount && (
                                            <div className="flex items-center gap-2">
                                                <Switch
                                                    checked={subscription.slack_subscribed && channelsOf(subscription).includes('slack')}
                                                    onCheckedChange={async () => {
                                                        // 구독 자체가 없으면 먼저 만들어야 채널만 켜도 발송되지 않는다
                                                        if (!subscription.slack_subscribed) {
                                                            try {
                                                                await fetch(`${API_BASE_URL}/subscription/${user.user_id}/slack/toggle`, {
                                                                    method: 'POST',
                                                                    headers: { 'Content-Type': 'application/json' },
                                                                    body: JSON.stringify({
                                                                        agent_name: subscription.agent_name,
                                                                        agent_version: subscription.agent_version
                                                                    })
                                                                });
                                                            } catch (error) {
                                                                console.error('Error toggling slack subscription:', error);
                                                                return;
                                                            }
                                                        }
                                                        await handleChannelToggle(subscription, 'slack');
                                                    }}
                                                />
                                                <span className="text-sm text-gray-700">Slack Notifications</span>
                                            </div>
                                            )}
                                            {(telegramLink?.linked || isTelegramAccount) && (
                                                <div className="flex items-center gap-2">
                                                    <Switch
                                                        checked={channelsOf(subscription).includes('telegram')}
                                                        onCheckedChange={async () => {
                                                            // 텔레그램 계정에는 Slack 스위치가 없다.
                                                            // 발송 대상 판정이 slack_subscriptions 행을 요구하므로
                                                            // 여기서 구독 행까지 만들어야 한다.
                                                            if (isTelegramAccount && !subscription.slack_subscribed) {
                                                                try {
                                                                    await fetch(`${API_BASE_URL}/subscription/${user.user_id}/slack/toggle`, {
                                                                        method: 'POST',
                                                                        headers: { 'Content-Type': 'application/json' },
                                                                        body: JSON.stringify({
                                                                            agent_name: subscription.agent_name,
                                                                            agent_version: subscription.agent_version
                                                                        })
                                                                    });
                                                                } catch (error) {
                                                                    console.error('Error creating subscription:', error);
                                                                    return;
                                                                }
                                                            }
                                                            await handleChannelToggle(subscription, 'telegram');
                                                        }}
                                                    />
                                                    <span className="text-sm text-gray-700">Telegram Notifications</span>
                                                </div>
                                            )}
                                            {/* 나중에 Email notification toggle 추가 예정 */}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </CardContent>
            </Card>
        </div>
    );
};